
import { Options, Vue } from "vue-class-component";
import {
  ScanPanelState,
  ResultsPanelFilter,
  ResultsPanelState,
  ScanPageScope,
  ScanPageState,
  HandoverV2Detail,
  HandoverV2DetailStt,
  DraftScanList
} from "@/domain/entities/HandoverV2";
import ScanPanel from "./components/ScanPanel.vue";
import ScanResultsFilter from "./components/ScanResultsFilter.vue";
import ScanResultsTable from "./components/ScanResultsTable.vue";
import ScanSidebarEntityPanel from "./components/ScanSidebarEntityPanel.vue";
import { PaginationV2 } from "@/domain/entities/Pagination";
import { HandoverV2Controller } from "@/app/ui/controllers/HandoverV2Controller";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { TrackingController } from "@/app/ui/controllers/TrackingController";
import { playNotification } from "@/app/infrastructures/misc/UtilsAudio";
import {
  TrackingSttData,
  TrackingSttDetailData
} from "@/domain/entities/TrackingStt";
import { HandoverV2DetailParams } from "@/data/payload/api/HandoverV2ApiRequest";
import {
  CLIENT_ERROR,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import {
  ErrorMessageEntities,
  ModalMessageEntities,
  Snackbar
} from "@/domain/entities/MainApp";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { HandoverController } from "@/app/ui/controllers/HandoverController";
import { ResponseUpdate } from "@/domain/entities/Handover";
import { UpdateApiRequest } from "@/data/payload/api/HandoverApiRequest";
import { FlagsPermissionHandover } from "@/feature-flags/flags-permission-handover";

@Options({
  components: {
    ScanPanel,
    ScanResultsFilter,
    ScanResultsTable,
    ScanSidebarEntityPanel
  },
  watch: {
    allDraftScanList: {
      handler(newVal: DraftScanList) {
        if (newVal.items.length < this.configMaxScanHandoverV2) {
          this.lastScanRow.isSuccess = Boolean(newVal.items.length);
          this.lastScanRow.error = "";
        }
      }
    }
  }
})
class UpdateHandoverV2 extends Vue {
  sttNoInputValue = "";
  isScanning = false;
  isDuplicateScan = false;
  lastScanRow = new HandoverV2Detail();
  failedScans = [] as HandoverV2DetailStt[];
  draftScanDataTable = new DraftScanList();
  allDraftScanList = new DraftScanList();
  eligibleVendorList = [] as HandoverV2Detail[];
  pagination = new PaginationV2({ limit: 10 });
  isLoading = false;
  pageState = new ScanPageState();
  page = new ScanPageScope();
  scanPanel = new ScanPanelState();
  resultsPanel = new ResultsPanelState();
  responseProcessHandover = new ResponseUpdate(0, 0, 0, []);

  mounted() {
    this.onReset();
  }

  onReset() {
    this.sttNoInputValue = "";
    this.isScanning = false;
    this.isDuplicateScan = false;
    this.lastScanRow = new HandoverV2Detail();
    this.failedScans = [];
    this.draftScanDataTable = new DraftScanList();
    this.allDraftScanList = new DraftScanList();
    this.eligibleVendorList = [] as HandoverV2Detail[];
    this.pagination = new PaginationV2({ limit: 10 });
    this.isLoading = false;
    this.responseProcessHandover = new ResponseUpdate(0, 0, 0, []);
  }

  get title() {
    return this.$route.meta.name as string;
  }

  get configMaxScanHandoverV2(): number {
    return FlagsPermissionHandover.config_max_scan_handover_v2.getValue();
  }

  async onScanEnter(sttNo: string) {
    this.isDuplicateScan = false;
    this.lastScanRow.error = "";

    if (!sttNo) return;

    if (this.allDraftScanList.items.length === this.configMaxScanHandoverV2) {
      this.lastScanRow.isSuccess = false;
      this.lastScanRow.error = this.$t("handoverV2.update.scan.errorMax", {
        max: this.configMaxScanHandoverV2
      });

      this.onResetScan();

      return;
    }

    try {
      this.isScanning = true;

      const sttNoOthers = await this.handleValidationSttOthers();
      const resp = await HandoverV2Controller.getDetailHandover(
        new HandoverV2DetailParams({
          sttNo: !this.checkIsStt(sttNo) ? sttNoOthers?.sttNo : sttNo,
          bagNo: sttNoOthers.bagNo ? sttNoOthers.bagNo : "",
          partnerCode: this.allDraftScanList.items.length
            ? this.lastScanRow.vendorCode
            : "",
          manifestKey: this.allDraftScanList.items.length
            ? this.lastScanRow?.manifestKey
            : ""
        })
      );

      this.addSttNumberToUpdate(resp);
    } catch (error) {
      this.handleFailedScan(
        new HandoverV2DetailStt({
          sttNo,
          message: parsingErrorResponse(error).message
        })
      );
      this.onResetScan();
    } finally {
      this.isScanning = false;
    }
  }

  checkIsStt(val: string) {
    return !!val.trim().match(/^\d{1,2}LP\w+$/g);
  }

  async handleValidationSttOthers(): Promise<HandoverV2DetailParams> {
    let sttNoOthers = new HandoverV2DetailParams();
    const isSTT = this.checkIsStt(this.sttNoInputValue);
    if (!isSTT) {
      if (
        this.sttNoInputValue.toLowerCase().startsWith("tkp") &&
        !this.sttNoInputValue.toLowerCase().includes("bag")
      ) {
        //TKP01-XXXXX
        sttNoOthers = await this.handleScanOthers(this.sttNoInputValue);
      } else {
        //TKP01-BAG-XXX or 3LC-XXXXX
        sttNoOthers = new HandoverV2DetailParams({
          sttNo: "",
          bagNo: this.sttNoInputValue
        });
      }
    }
    return sttNoOthers;
  }

  async handleScanOthers(sttNo: string) {
    MainAppController.showLoading();
    try {
      const resp = await TrackingController.trackStt({
        sttNo,
        isGetSla: false,
        isSecondCall: false,
        cache: true
      });

      if (resp) {
        return new HandoverV2DetailParams({
          sttNo: TrackingController.trackingSttData.sttDetail.sttNo
        });
      }

      // no stt
      this.handleFailedScan(
        new HandoverV2DetailStt({
          sttNo,
          message: this.$t("handoverV2.update.common.failedNotFound")
        })
      );

      return new HandoverV2DetailParams();
    } finally {
      TrackingController.setTrackingSttData(
        new TrackingSttData(new TrackingSttDetailData(), [])
      );
      MainAppController.closeLoading();
    }
  }

  addSttNumberToUpdate(response: HandoverV2Detail) {
    if (response.isAllowUpdateStatus) {
      if (response.bagStt?.length) {
        for (const stt of response.bagStt) {
          this.onValidateSttResult(response, stt);
        }
      } else {
        this.onValidateSttResult(response, response.stt);
      }
    } else {
      this.handleFailedScan(
        new HandoverV2DetailStt({
          sttNo: this.sttNoInputValue,
          message: response.errorMessage
        })
      );
    }
  }

  isSuccess = false;

  onValidateSttResult(response: HandoverV2Detail, stt: HandoverV2DetailStt) {
    this.isDuplicateScan = false;
    const exist = this.allDraftScanList.items.some(
      (data: HandoverV2Detail) => data.stt.sttNo === stt.sttNo
    );

    if (exist) {
      this.isDuplicateScan = true;
      playNotification("duplicate");
      this.lastScanRow = new HandoverV2Detail({
        ...response,
        stt,
        isSuccess: false
      });
      this.onResetScan();
    } else {
      if (response.isPaid) {
        playNotification("success");
        this.handleSuccessScan(
          new HandoverV2Detail({
            ...response,
            stt
          })
        );

        this.lastScanRow = new HandoverV2Detail({
          ...response,
          isSuccess: true
        });

        return;
      }

      this.handleFailedScan(
        new HandoverV2DetailStt({
          sttNo: this.sttNoInputValue,
          message: this.$t("handoverV2.update.common.failedUnpaid")
        })
      );
    }
  }

  getReasonPriority(vendor: HandoverV2Detail): number {
    const text = `${vendor.reason || ""} ${vendor.reasonEn ||
      ""}`.toLowerCase();
    if (text.includes("duplicate") || text.includes("duplikat")) return 1;
    if (text.includes("commodity") || text.includes("komoditas")) return 2;
    if (text.includes("piece") || text.includes("koli")) return 3;
    return 4;
  }

  getEligibleVendorList() {
    const { items } = this.allDraftScanList;

    if (!items.length) return (this.eligibleVendorList = []);

    const vendorsByCode = new Map<string, HandoverV2Detail>();

    items.forEach(({ eligibleVendors }) => {
      eligibleVendors.forEach((vendor) => {
        const existing = vendorsByCode.get(vendor.vendorCode);
        const shouldReplace =
          !existing ||
          (existing.isAvailable && !vendor.isAvailable) ||
          (!existing.isAvailable &&
            !vendor.isAvailable &&
            this.getReasonPriority(vendor) < this.getReasonPriority(existing));

        if (shouldReplace) {
          vendorsByCode.set(vendor.vendorCode, vendor);
        }
      });
    });

    this.eligibleVendorList = [...vendorsByCode.values()].sort((a, b) => {
      if (b.isAvailable !== a.isAvailable) {
        return Number(b.isAvailable) - Number(a.isAvailable);
      }
      if (!a.isAvailable && !b.isAvailable) {
        return this.getReasonPriority(a) - this.getReasonPriority(b);
      }
      return 0;
    });
  }

  get isFiltered() {
    return Object.values(this.resultsPanel.filters).some(Boolean);
  }

  handleSuccessScan(item: HandoverV2Detail) {
    if (this.isFiltered) {
      this.resultsPanel.filters = new ResultsPanelFilter();
      this.draftScanDataTable.items = [item, ...this.allDraftScanList.items];
    } else {
      this.draftScanDataTable.items = [item, ...this.draftScanDataTable.items];
    }

    this.onUpdateDraftScanListTemp();
    this.getEligibleVendorList();

    this.onResetScan();
  }

  onUpdateDraftScanListTemp() {
    this.allDraftScanList = new DraftScanList({ ...this.draftScanDataTable });
  }

  onResetScan() {
    this.sttNoInputValue = "";
  }

  getLastScanRow(isSuccess = true) {
    const lastData = new HandoverV2Detail(this.allDraftScanList.items?.[0]);

    this.lastScanRow = new HandoverV2Detail({
      ...this.lastScanRow,
      stt: new HandoverV2DetailStt({
        ...this.lastScanRow.stt,
        sttNo: isSuccess ? lastData.stt.sttNo : this.sttNoInputValue
      }),
      eligibleVendors: lastData.eligibleVendors,
      isSuccess
    });
  }

  handleFailedScan(stt: HandoverV2DetailStt) {
    this.isDuplicateScan = false;
    this.failedScans = [stt, ...this.failedScans];
    playNotification("error");

    this.getLastScanRow(false);
    this.onResetScan();
  }

  onFiltersUpdate(filter: Partial<ResultsPanelFilter>): void {
    this.draftScanDataTable.items = this.allDraftScanList.items.filter(
      (item) => {
        const keywordMatch =
          !filter.keyword ||
          item.stt.sttNo.toLowerCase().includes(filter.keyword.toLowerCase()) ||
          item.stt.sttRecipientName
            .toLowerCase()
            .includes(filter.keyword.toLowerCase());

        const productMatch =
          !filter.product || item.stt.sttProductType === filter.product;

        const commodityMatch =
          !filter.commodity || item.stt.sttCommodityCode === filter.commodity;

        return keywordMatch && productMatch && commodityMatch;
      }
    );
  }

  onDeleteRow(row: HandoverV2Detail): void {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: this.$t("handoverV2.update.table.remove.confirmTitle", {
          sttNo: row.stt.sttNo
        }),
        message: this.$t("handoverV2.update.table.remove.confirmMessage"),
        textSuccess: this.$t(
          "handoverV2.update.table.remove.confirmButtonText"
        ),
        textCancel: this.$t("handoverV2.update.common.back"),
        onSubmit: () => {
          // get from all data even if filtered
          this.draftScanDataTable.items = this.allDraftScanList.items.filter(
            (item) => item.stt.sttNo !== row.stt.sttNo
          );
          this.onUpdateDraftScanListTemp();
          this.getLastScanRow();
          this.getEligibleVendorList();

          // reset filters each after delete
          this.resultsPanel.filters = new ResultsPanelFilter();

          MainAppController.closeMessageModal();

          MainAppController.setSnackbarData(
            new Snackbar({
              type: "success",
              message: `<span class="font-semibold text-black-lp-100"> ${this.$t(
                "handoverV2.update.table.remove.success",
                {
                  sttNo: row.stt.sttNo
                }
              )} </span>`
            })
          );
          MainAppController.setAutoClose(true);
          MainAppController.setSnackbar(true);
        },
        onClose: () => {
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  onConfirmProcessHandover() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: this.$t("handoverV2.update.process.confirmTitle"),
        message: this.$t("handoverV2.update.process.confirmMessage"),
        textSuccess: this.$t("handoverV2.update.process.confirmButtonText"),
        textCancel: this.$t("handoverV2.update.common.back"),
        onSubmit: () => {
          MainAppController.closeMessageModal();
          this.onProcessHandover();
        },
        onClose: () => {
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  async onProcessHandover() {
    const res: ResponseUpdate = await HandoverController.onUpdate(
      new UpdateApiRequest({
        destinationPartnerId: this.lastScanRow.destinationPartnerId,
        destinationPartnerCode: this.lastScanRow.destinationPartnerCode,
        destinationPartnerType: this.lastScanRow.destinationPartnerType,
        destinationCityCode: this.allDraftScanList.items?.[0]?.stt
          .sttDestinationCityId,
        remarks: this.lastScanRow.remarks,
        handoverSttNo: this.allDraftScanList.items.map(
          (e: HandoverV2Detail) => e.stt.sttNo
        )
      })
    );

    this.responseProcessHandover = res;

    if (res) {
      if (res.totalSttSuccess > 0 && res.totalSttFailed > 0) {
        this.onPartialSuccessProcessHandover();
        return;
      }

      if (
        res.totalSttSuccess > 0 &&
        (!res.totalSttFailed || res.totalSttFailed === 0)
      ) {
        this.onSuccessProcessHandover();
        return;
      }

      MainAppController.showErrorMessage(
        new ErrorMessageEntities({
          type: CLIENT_ERROR,
          title: this.$t("handoverV2.update.errorProcess.title"),
          message: this.$t("handoverV2.update.errorProcess.message"),
          onClose: () => {
            MainAppController.closeErrorMessage();
            this.onReset();
          }
        })
      );
    }
  }
  onSuccessProcessHandover() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-success",
        imageWidth: "32",
        imageHeight: "32",
        title: this.$t("handoverV2.update.successProcess.title"),
        message: `<span class="font-bold">${
          this.responseProcessHandover.totalSttSuccess
        } STT</span> ${this.$t("handoverV2.update.successProcess.message")}`,
        textSuccess: this.$t("handoverV2.update.common.ok"),
        textCancel: this.$t("handoverV2.update.common.printManifest"),
        iconCancel: "ics_o_print_red",
        onSubmit: () => {
          MainAppController.closeMessageModal();
          this.$router.push("/handover-v2");
        },
        onClose: () => {
          this.onPrintManifest();
        },
        customButton: "w-full text-sm",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-6"
      })
    );
  }
  onPartialSuccessProcessHandover() {
    const total =
      (this.responseProcessHandover.totalSttSuccess || 0) +
      (this.responseProcessHandover.totalSttFailed || 0);
    const totalSuccess = this.responseProcessHandover.totalSttSuccess || 0;
    const totalFailed = this.responseProcessHandover.totalSttFailed || 0;

    const message = `
      <div class="border border-gray-lp-1400 rounded-lg py-3 px-2 flex flex-row items-center text-center my-2">
        <div class="flex-1">
          <div class="text-xs text-gray-lp-600">${this.$t(
            "handoverV2.update.partialSuccessProcess.total"
          )}</div>
          <div class="text-sm font-semibold text-black-lp-100">${total} STT</div>
        </div>
        <div class="flex-1 border-l border-r border-gray-lp-1400">
          <div class="text-xs text-gray-lp-600">${this.$t(
            "handoverV2.update.partialSuccessProcess.success"
          )}</div>
          <div class="text-sm font-semibold text-black-lp-100">${totalSuccess} STT</div>
        </div>
        <div class="flex-1">
          <div class="text-xs text-gray-lp-600">${this.$t(
            "handoverV2.update.partialSuccessProcess.failed"
          )}</div>
          <div class="text-sm font-semibold text-red-lp-2400">${totalFailed} STT</div>
        </div>
      </div>
    `.trim();

    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-success",
        imageWidth: "32",
        imageHeight: "32",
        title: this.$t("handoverV2.update.partialSuccessProcess.title"),
        message,
        textSuccess: this.$t("handoverV2.update.common.ok"),
        textCancel: this.$t("handoverV2.update.common.printManifest"),
        iconCancel: "ics_o_print_red",
        onSubmit: () => {
          MainAppController.closeMessageModal();
          this.$router.push("/handover-v2");
        },
        onClose: () => {
          this.onPrintManifest();
        },
        customButton: "w-full text-sm",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-6"
      })
    );
  }

  async onPrintManifest() {
    const accountType = AccountController.accountData.account_type_detail.type;
    const print: any = await import(
      "@/app/ui/views/incoming-process/handover/modules/print.vue"
    );
    print.default.methods.print(
      accountType,
      this.responseProcessHandover.handoverId
    );
  }

  onClearAll() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: this.$t("handoverV2.update.cancelProcess.confirmTitle"),
        message: this.$t("handoverV2.update.cancelProcess.confirmMessage"),
        textSuccess: this.$t(
          "handoverV2.update.cancelProcess.confirmButtonText"
        ),
        textCancel: this.$t("handoverV2.update.common.back"),
        onSubmit: () => {
          this.onReset();

          MainAppController.setSnackbarData(
            new Snackbar({
              type: "success",
              message: this.$t("handoverV2.update.cancelProcess.success")
            })
          );
          MainAppController.setAutoClose(true);
          MainAppController.setSnackbar(true);

          MainAppController.closeMessageModal();
        },
        onClose: () => {
          MainAppController.closeMessageModal();
        }
      })
    );
  }
}

export default UpdateHandoverV2;
